import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2, Send } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppButton";
import { COMPANY } from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-slate-800 rounded-md px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)] transition-[border-color,box-shadow]";

export default function Contact() {
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé ! Je vous réponds au plus vite.");
      setForm({ nom: "", prenom: "", email: "", telephone: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Une erreur est survenue. Réessayez ou contactez-moi sur WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-32 pb-28 px-6 lg:px-10 relative overflow-hidden" data-testid="contact-page">
      <div aria-hidden="true" className="absolute top-40 -right-40 w-[480px] h-[480px] rounded-full bg-blue-600/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto relative">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-blue-500 mb-6">Contact</p>
        <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-[5rem] tracking-tighter leading-[0.95]" data-testid="contact-title">
          <KineticText delay={0.15} lines={[<>Parlons de</>, <><span className="text-blue-500">votre projet.</span></>]} />
        </h1>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* FORM */}
          <Reveal className="lg:col-span-3" delay={0.1}>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nom" className="block font-mono text-xs tracking-wide uppercase text-slate-500 mb-2">Nom</label>
                  <input id="nom" data-testid="contact-input-nom" className={inputCls} placeholder="Votre nom" value={form.nom} onChange={set("nom")} required maxLength={100} />
                </div>
                <div>
                  <label htmlFor="prenom" className="block font-mono text-xs tracking-wide uppercase text-slate-500 mb-2">Prénom</label>
                  <input id="prenom" data-testid="contact-input-prenom" className={inputCls} placeholder="Votre prénom" value={form.prenom} onChange={set("prenom")} required maxLength={100} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block font-mono text-xs tracking-wide uppercase text-slate-500 mb-2">Email</label>
                  <input id="email" data-testid="contact-input-email" type="email" className={inputCls} placeholder="vous@exemple.com" value={form.email} onChange={set("email")} required />
                </div>
                <div>
                  <label htmlFor="telephone" className="block font-mono text-xs tracking-wide uppercase text-slate-500 mb-2">Téléphone</label>
                  <input id="telephone" data-testid="contact-input-telephone" type="tel" className={inputCls} placeholder="06 12 34 56 78" value={form.telephone} onChange={set("telephone")} required minLength={6} maxLength={25} />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block font-mono text-xs tracking-wide uppercase text-slate-500 mb-2">Message</label>
                <textarea id="message" data-testid="contact-input-message" rows={6} className={inputCls} placeholder="Décrivez votre projet…" value={form.message} onChange={set("message")} required minLength={10} maxLength={5000} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold px-8 py-4 transition-colors"
              >
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} className="transition-transform group-hover:translate-x-0.5" />}
                {sending ? "Envoi en cours…" : "Envoyer le message"}
              </button>
            </form>
          </Reveal>

          {/* INFO */}
          <Reveal className="lg:col-span-2" delay={0.2}>
            <div className="border border-slate-800/80 p-8 lg:p-10 space-y-8">
              <div>
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500 mb-6">Coordonnées</p>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <Phone size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <div>
                      <a href="tel:+33669083803" data-testid="contact-phone" className="text-slate-200 hover:text-white font-medium transition-colors">{COMPANY.phone}</a>
                      <p className="text-xs text-slate-500 mt-0.5">Téléphone & WhatsApp</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <Mail size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="text-slate-200 hover:text-white font-medium transition-colors">{COMPANY.email}</a>
                  </li>
                  <li className="flex items-start gap-4">
                    <MapPin size={18} className="text-blue-500 mt-0.5 shrink-0" />
                    <span className="text-slate-200 font-medium" data-testid="contact-address">{COMPANY.address}</span>
                  </li>
                </ul>
              </div>

              <div className="border-t border-slate-800/80 pt-8">
                <p className="text-sm text-slate-400 mb-5 leading-relaxed">
                  Une réponse plus rapide ? Écrivez-moi directement sur WhatsApp.
                </p>
                <a
                  href={COMPANY.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="inline-flex items-center gap-3 rounded-full bg-[#25d366] hover:bg-[#1da851] text-white font-semibold px-7 py-3.5 transition-colors w-full justify-center sm:w-auto"
                >
                  <WhatsAppIcon size={20} />
                  Contactez-moi sur WhatsApp
                </a>
              </div>

              <div className="border-t border-slate-800/80 pt-6">
                <p className="font-mono text-[11px] text-slate-600 leading-relaxed">
                  {COMPANY.name} — {COMPANY.owner}<br />
                  SIRET {COMPANY.siret}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
